import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { getUserByUsername } from '../../util/api';
import { parse } from '../../util/urlHelpers';
import { filterCountsFromResponse } from '../../util/filterCounts';
import { isVimeoUrl, extractVimeoVideoId } from '../../components/FieldRichTextInput/nodes/VimeoEmbedNode';

const { UUID } = sdkTypes;

// A shop's listings come a page at a time (the tab pages through them); its reviews and
// filter counts are read from every page, up to MAX_PAGES of Sharetribe's 100 a page.
export const LISTINGS_PAGE_SIZE = 48;
const PAGE_MAX = 100;
const MAX_PAGES = 10;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

export const FETCH_FILTER_COUNTS_REQUEST = 'app/ProfilePage/FETCH_FILTER_COUNTS_REQUEST';
export const FETCH_FILTER_COUNTS_SUCCESS = 'app/ProfilePage/FETCH_FILTER_COUNTS_SUCCESS';
export const FETCH_FILTER_COUNTS_ERROR = 'app/ProfilePage/FETCH_FILTER_COUNTS_ERROR';

export const SET_VIMEO_THUMBNAIL = 'app/ProfilePage/SET_VIMEO_THUMBNAIL';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsInProgress: true,
  queryListingsError: null,
  // The latest listings query asked for (filters and words change it): an older answer
  // arriving after it is dropped.
  queryListingsRequest: 0,
  // The listings query's page, pages and total (Sharetribe's meta), for the tab's paging
  listingsPagination: null,
  reviews: [],
  queryReviewsInProgress: true,
  queryReviewsError: null,
  filterCounts: {},
  filterCountsInProgress: false,
  filterCountsError: null,
  // The latest count asked for: an older answer (another shop's) arriving after it is dropped.
  filterCountsRequest: 0,
  vimeoThumbnailUrl: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      // Another shop: any count still on its way (request 0 matches none) is dropped
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,
        queryListingsInProgress: true,
        queryListingsRequest: action.meta?.request ?? state.queryListingsRequest,
        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],
        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      if (action.meta?.request !== state.queryListingsRequest) return state;
      return {
        ...state,
        queryListingsInProgress: false,
        userListingRefs: payload.listingRefs,
        listingsPagination: payload.pagination || null,
      };
    case QUERY_LISTINGS_ERROR:
      if (action.meta?.request !== state.queryListingsRequest) return state;
      return {
        ...state,
        queryListingsInProgress: false,
        userListingRefs: [],
        listingsPagination: null,
        queryListingsError: payload,
      };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsInProgress: true, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, queryReviewsInProgress: false, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, queryReviewsInProgress: false, reviews: [], queryReviewsError: payload };

    case FETCH_FILTER_COUNTS_REQUEST:
      return {
        ...state,
        filterCountsInProgress: true,
        filterCountsError: null,
        filterCountsRequest: payload,
      };
    case FETCH_FILTER_COUNTS_SUCCESS:
      return action.meta?.request !== state.filterCountsRequest
        ? state
        : { ...state, filterCounts: payload, filterCountsInProgress: false };
    case FETCH_FILTER_COUNTS_ERROR:
      return action.meta?.request !== state.filterCountsRequest
        ? state
        : { ...state, filterCountsError: payload, filterCountsInProgress: false };

    case SET_VIMEO_THUMBNAIL:
      return { ...state, vimeoThumbnailUrl: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = (userId, request) => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
  meta: { request },
});

export const queryListingsSuccess = (listingRefs, request, pagination = null) => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs, pagination },
  meta: { request },
});

export const queryListingsError = (e, request) => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const fetchFilterCountsRequest = request => ({
  type: FETCH_FILTER_COUNTS_REQUEST,
  payload: request,
});

export const fetchFilterCountsSuccess = (request, counts) => ({
  type: FETCH_FILTER_COUNTS_SUCCESS,
  payload: counts,
  meta: { request },
});

export const fetchFilterCountsError = (request, error) => ({
  type: FETCH_FILTER_COUNTS_ERROR,
  payload: error,
  meta: { request },
});

// ================ Thunks ================ //

// Every page of a query (100 a page, at most MAX_PAGES), first page first
const allPages = async (query, params) => {
  const first = await query({ ...params, page: 1, perPage: PAGE_MAX });
  const totalPages = Math.min(first.data.meta?.totalPages || 1, MAX_PAGES);
  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      query({ ...params, page: i + 2, perPage: PAGE_MAX })
    )
  );
  return [first, ...rest];
};

// The page asked for in the URL: a whole number from 1, else the first
const pageFrom = page => {
  const number = Number(page);
  return Number.isInteger(number) && number > 0 ? number : 1;
};

let queryListingsRequests = 0;
export const queryUserListings = (userId, searchParams, config) => (dispatch, getState, sdk) => {
  queryListingsRequests += 1;
  const request = queryListingsRequests;
  dispatch(queryListingsRequest(userId, request));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Extract keywords, showSoldOut, and filter params from searchParams.
  // showSoldOut is a UI-only URL param — extracted here so it never reaches the
  // Sharetribe API as an unknown param. See SearchPage.duck.js for the same pattern.
  const { keywords, showSoldOut, page, ...filterParams } = searchParams || {};

  // Mirror SearchPage's default behavior: hide sold-out listings unless the user
  // explicitly enables the "Show Sold Out Items" filter. stockMode 'match-undefined'
  // ensures bookable/inquiry listings (no stock data) are always included.
  // Note: parse() in urlHelpers converts the string 'true' to boolean true, so both
  // forms are checked.
  // When showSoldOut is active we query pub_soldOut=true (publicData flag) to return
  // ONLY sold-out listings. See soldOutSync.js for how the flag is kept in sync.
  const stockParams = showSoldOut === true || showSoldOut === 'true'
    ? { pub_soldOut: true }
    : { minStock: 1, stockMode: 'match-undefined' };

  return sdk.listings
    .query({
      author_id: userId,
      keywords: keywords || undefined,
      ...filterParams,
      ...stockParams,
      page: pageFrom(page),
      perPage: LISTINGS_PAGE_SIZE,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs, request, response.data.meta));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e), request)));
};

// Every page of the user's reviews. Returned, so the server render waits for them (and
// the page's rating schema).
export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  return allPages(params => sdk.reviews.query(params), {
    subject_id: userId,
    state: 'public',
    include: ['author', 'author.profileImage', 'listing'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    'fields.listing': ['title'],
  })
    .then(responses => {
      const reviews = responses.flatMap(response => denormalisedResponseEntities(response));
      dispatch(queryReviewsSuccess(reviews));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

// The shop's filter counts: from every page of its listings, kept only when those are all
// of them (filterCountsFromResponse)
let filterCountsRequests = 0;
export const fetchFilterCounts = (userId, filterConfigs) => (dispatch, getState, sdk) => {
  filterCountsRequests += 1;
  const request = filterCountsRequests;
  dispatch(fetchFilterCountsRequest(request));

  // Fetch user's listings with only publicData for counting
  const countParams = {
    author_id: userId,
    minStock: 1,
    stockMode: 'match-undefined',
    'fields.listing': ['publicData'],
  };

  return allPages(params => sdk.listings.query(params), countParams)
    .then(responses => {
      const response = {
        data: {
          data: responses.flatMap(r => r.data.data),
          meta: { totalItems: responses[0].data.meta?.totalItems },
        },
      };
      const counts = filterCountsFromResponse(response, filterConfigs);
      dispatch(fetchFilterCountsSuccess(request, counts));
      return counts;
    })
    .catch(e => {
      dispatch(fetchFilterCountsError(request, storableError(e)));
    });
};

export const showUser = userId => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const { username } = params;

  // Parse search params for filters
  const searchParams = parse(search);
  // Separate tab/reviewType params from filter params
  const { tab, reviewType, ...filterParams } = searchParams;

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  try {
    // First, get user by username to find their ID. Users without a username
    // are linked by their id (see profileSlug), which needs no lookup.
    const userId = UUID_PATTERN.test(username)
      ? new UUID(username)
      : new UUID((await getUserByUsername(username)).data.userId);

    const result = await Promise.all([
      dispatch(fetchCurrentUser()),
      dispatch(showUser(userId)),
      dispatch(queryUserListings(userId, filterParams, config)),
      dispatch(queryUserReviews(userId)),
    ]);

    // Fetch Vimeo thumbnail server-side so it's available for OG meta tags
    const state = getState();
    const userMatches = state.marketplaceData?.entities?.user;
    const userData = userMatches ? Object.values(userMatches).find(u => u.id?.uuid === userId.uuid) : null;
    const shopVideoUrl = userData?.attributes?.profile?.publicData?.shopVideoUrl;
    if (shopVideoUrl && isVimeoUrl(shopVideoUrl)) {
      const videoId = extractVimeoVideoId(shopVideoUrl);
      if (videoId) {
        try {
          const vimeoRes = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`);
          if (vimeoRes.ok) {
            const vimeoData = await vimeoRes.json();
            if (vimeoData.thumbnail_url) {
              dispatch({ type: SET_VIMEO_THUMBNAIL, payload: vimeoData.thumbnail_url });
            }
          }
        } catch (e) {
          // Vimeo thumbnail is non-critical; silently ignore errors
        }
      }
    }

    // After main data loads, fetch filter counts
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};
    const allFilterConfigs = [...(defaultFiltersConfig || []), ...(listingFieldsConfig || [])];

    if (allFilterConfigs.length > 0) {
      dispatch(fetchFilterCounts(userId, allFilterConfigs));
    }

    return result;
  } catch (error) {
    // If user not found by username, dispatch error
    const storableErr = storableError(error);
    dispatch(showUserError(storableErr));
    throw error;
  }
};
